import React, {
  forwardRef,
  useCallback,
  useRef,
  useEffect,
  useState,
  CSSProperties,
  ReactNode,
  HTMLAttributes,
} from "react";

import {
  createPopper,
  Instance,
  Modifier,
  Options as PopperOptions,
  PositioningStrategy,
  State,
} from "@popperjs/core";

import { Placement } from ".";
import { StyledPopper } from "./Styles";
import useForkRef from "../../utils/useForkRef";
type CustomModifier = Modifier<string, PopperOptions>;
interface TransitionProps {
  in: boolean;
  onEnter: () => void;
  onExited: () => void;
}
interface IChildProps {
  placement: Placement;
  transitionProps: Partial<TransitionProps>;
}

interface PopperProps extends Omit<HTMLAttributes<HTMLDivElement>, "children"> {
  /** Anchor Element. */
  anchorEl?: Element | null;
  /** Open */
  open?: boolean;
  /** Inline style */
  style?: CSSProperties;
  /** Child with transition */
  transition?: boolean;
  /** Placement */
  placement?: Placement;
  /** Strategy */
  strategy?: PositioningStrategy;
  /** After Write callback */
  afterWrite?: (...args) => void;

  /** Component children */
  children?: ReactNode | React.FC<Partial<IChildProps>>;
}

export const Popper = forwardRef<HTMLDivElement, PopperProps>(
  (
    {
      children,
      anchorEl,
      open = false,
      style,
      transition = false,
      placement: initialPlacement = "bottom",
      strategy = "absolute",
      afterWrite,
      ...rest
    },
    ref
  ) => {
    const [exited, setExited] = useState(true);
    const [placement, setPlacement] = useState(initialPlacement);
    const ownRef = useRef<HTMLDivElement | null>(null);
    const popperRef = useRef<Instance>();

    const handleInnerRef = useForkRef(ownRef, ref);

    const handlePopperUpdate = (data: Partial<State>): void => {
      setPlacement(data.placement || "bottom");
    };

    const handleOpen = useCallback(() => {
      if (!ownRef.current || !anchorEl || !open) {
        return;
      }

      if (popperRef.current) {
        popperRef.current.destroy();
      }

      const modifiers: CustomModifier[] = [
        {
          name: "hide",
          enabled: true,
          phase: "main",
          fn: () => {
            const referenceElement = anchorEl as HTMLElement;

            let currentElement: HTMLElement | null = referenceElement;
            let isHidden = false;
            while (currentElement) {
              const computedStyle = getComputedStyle(currentElement);
              if (
                computedStyle.display === "none" ||
                computedStyle.visibility === "hidden"
              ) {
                isHidden = true;
                break;
              }
              currentElement = currentElement.parentElement;
            }

            if (isHidden) {
              ownRef.current?.setAttribute("data-popper-reference-hidden", "");
            } else {
              ownRef.current?.removeAttribute("data-popper-reference-hidden");
            }
          },
        },
      ];

      if (typeof afterWrite === "function") {
        modifiers.push({
          phase: "afterWrite",
          enabled: true,
          name: "popper-after-write",
          fn: afterWrite,
        });
      }

      popperRef.current = createPopper(anchorEl, ownRef.current, {
        placement,
        onFirstUpdate: handlePopperUpdate,
        strategy,
        modifiers,
      });
    }, [afterWrite, anchorEl, open, placement, strategy]);

    const handleClose = (): void => {
      if (!popperRef.current) {
        return;
      }

      popperRef.current.destroy();
    };

    const handleRef = useCallback(
      (node: HTMLDivElement) => {
        handleInnerRef(node);
        handleOpen();
      },
      [handleOpen, handleInnerRef]
    );

    const handleEnter = (): void => {
      setExited(false);
    };

    const handleExited = (): void => {
      setExited(true);
      handleClose();
    };

    useEffect(() => {
      return (): void => {
        handleClose();
      };
    }, []);

    useEffect(() => {
      if (!open && !transition) {
        handleClose();
      }
    }, [open, transition]);

    useEffect(() => {
      if (popperRef.current) {
        popperRef.current.update();
      }
    });

    if (!open && (!transition || exited)) {
      return null;
    }

    const childProps: Partial<IChildProps> = { placement };

    if (transition) {
      childProps.transitionProps = {
        in: open,
        onEnter: handleEnter,
        onExited: handleExited,
      };
    }

    return (
      <StyledPopper
        ref={handleRef}
        {...rest}
        style={{
          ...style,
        }}
      >
        {typeof children === "function" ? children(childProps) : children}
      </StyledPopper>
    );
  }
);

Popper.displayName = "Popper";
